import {
  getActionsForPage,
  getAvailablePages,
  getModuleForPage,
  getPageDef,
  getPageKeyByPath,
  getPagePathByKey,
  isPageLicensed,
} from '@repo/permissions';

import {
  buildTenantPermissionMap,
  getEffectivePermissionsForPage,
  resolveTenantMembership,
} from '../../../lib/tenant-role-permissions.js';

export const SESSION_ROLE_PREVIEW = '__session__';

const ACTION_LABELS = Object.freeze({
  View: 'View',
  Add: 'Add',
  Update: 'Update',
  Delete: 'Delete',
  Export: 'Export',
  Approve: 'Approve',
  Assign: 'Assign',
  Close: 'Close',
});

export function normalizeId(value) {
  if (value == null) return null;
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value.id != null) return String(value.id);
    if (value._id != null) return String(value._id);
    if (typeof value.toString === 'function' && value.toString !== Object.prototype.toString) {
      const text = value.toString();
      return text === '[object Object]' ? null : text;
    }
  }
  return null;
}

export function getTenantIdFromMembership(membership) {
  return normalizeId(membership?.tenant) || normalizeId(membership?.tenantId);
}

export function getTenantNameFromMembership(membership) {
  const tenant = membership?.tenant;
  if (!tenant || typeof tenant !== 'object') return getTenantIdFromMembership(membership) || 'Tenant yok';
  return tenant.name || tenant.displayName || tenant.slug || normalizeId(tenant) || 'Tenant yok';
}

export function getRoleId(role) {
  return normalizeId(role);
}

export function getRoleName(role) {
  if (!role || typeof role !== 'object') return String(role || 'Rol yok');
  return role.name || role.label || role.code || getRoleId(role) || 'Rol yok';
}

export function getActionLabel(action) {
  return ACTION_LABELS[action] || action;
}

export function getUserDisplayName(user) {
  if (!user) return 'No user';
  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  return user.name || fullName || user.userName || user.email || normalizeId(user) || 'No user';
}

export function getMemberships(user) {
  return Array.isArray(user?.memberships) ? user.memberships.filter(Boolean) : [];
}

export function getMembershipRoles(membership) {
  return Array.isArray(membership?.roles) ? membership.roles.filter(Boolean) : [];
}

export function buildRolePreviewUser(user, selectedTenantId, previewRoleId) {
  if (!user || !previewRoleId || previewRoleId === SESSION_ROLE_PREVIEW) return user;

  const memberships = getMemberships(user);
  const targetMembership = resolveTenantMembership(user, selectedTenantId);
  const targetTenantId = getTenantIdFromMembership(targetMembership);
  const nextMemberships = memberships.map((membership) => {
    const membershipTenantId = getTenantIdFromMembership(membership);
    if (membershipTenantId !== targetTenantId) return membership;

    const roles = getMembershipRoles(membership).filter((role) => getRoleId(role) === previewRoleId);
    return {
      ...membership,
      roles,
      role: roles[0],
    };
  });

  const nextTarget = nextMemberships.find((membership) => getTenantIdFromMembership(membership) === targetTenantId);
  const roles = getMembershipRoles(nextTarget);

  return {
    ...user,
    memberships: nextMemberships,
    roles,
    role: roles[0],
    unit: nextTarget?.unit ?? user.unit,
  };
}

export function buildRolePermissionPages(permissionMap, licensedModules = []) {
  return getAvailablePages(licensedModules)
    .map((pageKey) => {
      const actions = getActionsForPage(pageKey);
      const effectivePermissions = getEffectivePermissionsForPage(permissionMap, pageKey);
      const actionRows = actions.map((action) => ({
        action,
        label: getActionLabel(action),
        allowed: effectivePermissions.includes(action),
      }));
      const allowedCount = actionRows.filter((row) => row.allowed).length;
      const pageDef = getPageDef(pageKey);

      return {
        pageKey,
        pageTitle: pageDef?.label || pageKey,
        pagePath: getPagePathByKey(pageKey),
        pageModule: getModuleForPage(pageKey),
        actions: actionRows,
        allowedCount,
        totalCount: actionRows.length,
        hasAnyPermission: allowedCount > 0,
      };
    })
    .filter((page) => page.totalCount > 0);
}

export function buildAccessLabSnapshot({ user, selectedTenantId, pathname, previewRoleId = SESSION_ROLE_PREVIEW } = {}) {
  const effectiveUser = buildRolePreviewUser(user, selectedTenantId, previewRoleId);
  const membership = resolveTenantMembership(effectiveUser, selectedTenantId);
  const roles = getMembershipRoles(membership);
  const licensedModules = Array.isArray(membership?.tenant?.settings?.modules)
    ? membership.tenant.settings.modules
    : [];
  const permissionMap = buildTenantPermissionMap(membership, effectiveUser);
  const rolePermissionPages = buildRolePermissionPages(permissionMap, licensedModules);
  const cleanPath = pathname ? pathname.split('?')[0].split('#')[0] : '';
  const pageKey = getPageKeyByPath(cleanPath);
  const pageDef = pageKey ? getPageDef(pageKey) : null;
  const registryPath = pageKey ? getPagePathByKey(pageKey) : undefined;
  const pageModule = pageKey ? getModuleForPage(pageKey) : null;
  const actions = pageKey ? getActionsForPage(pageKey) : [];
  const effectivePermissions = pageKey ? getEffectivePermissionsForPage(permissionMap, pageKey) : [];
  const pageLicensed = pageKey ? isPageLicensed(pageKey, licensedModules) : false;
  const rows = actions.map((action) => {
    const roleGranted = effectivePermissions.includes(action);
    const allowed = Boolean(pageKey && pageLicensed && roleGranted);
    let reason = 'İzin yok';
    if (!pageKey) reason = 'Route registry eşleşmesi yok';
    else if (!pageLicensed) reason = 'Modül lisanslı değil';
    else if (roleGranted) reason = 'RBAC izni var';

    return {
      action,
      label: getActionLabel(action),
      allowed,
      roleGranted,
      pageLicensed,
      reason,
    };
  });

  return {
    user: effectiveUser,
    originalUser: user,
    userDisplayName: getUserDisplayName(effectiveUser),
    selectedTenantId: selectedTenantId || null,
    membership,
    tenantId: getTenantIdFromMembership(membership),
    tenantName: getTenantNameFromMembership(membership),
    roles,
    roleNames: roles.map(getRoleName),
    licensedModules,
    permissionMap,
    pathname: cleanPath,
    pageKey,
    pageTitle: pageDef?.label || pageKey || 'Bilinmeyen sayfa',
    registryPath,
    pageModule,
    pageLicensed,
    effectivePermissions,
    actions: rows,
    rolePermissionPages,
    previewRoleId,
    previewMode: Boolean(previewRoleId && previewRoleId !== SESSION_ROLE_PREVIEW),
  };
}

export function buildCopyContext(snapshot) {
  if (!snapshot) return {};
  return {
    accessLab: true,
    previewMode: snapshot.previewMode,
    user: {
      id: normalizeId(snapshot.user),
      name: snapshot.userDisplayName,
      email: snapshot.user?.email || null,
    },
    tenant: {
      id: snapshot.tenantId,
      name: snapshot.tenantName,
    },
    route: {
      pathname: snapshot.pathname,
      pageKey: snapshot.pageKey,
      registryPath: snapshot.registryPath,
      module: snapshot.pageModule,
      licensed: snapshot.pageLicensed,
    },
    roles: snapshot.roles.map((role) => ({
      id: getRoleId(role),
      name: getRoleName(role),
    })),
    licensedModules: snapshot.licensedModules,
    effectivePermissions: snapshot.effectivePermissions,
    actions: snapshot.actions.map(({ action, allowed, reason }) => ({ action, allowed, reason })),
    rolePermissions: snapshot.rolePermissionPages
      .filter((page) => page.hasAnyPermission)
      .map((page) => ({
        pageKey: page.pageKey,
        pagePath: page.pagePath,
        permissions: page.actions.filter((row) => row.allowed).map((row) => row.action),
      })),
  };
}
